import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, ArrowRight } from 'lucide-react';

// Renders one NIL lesson: markdown body + key takeaways + "Mark Complete → Next"
export default function ApexLesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [nextLessonId, setNextLessonId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLesson(null);
      setErr('');
      setCompleted(false);
      try {
        const [lRes, cRes] = await Promise.all([
          api.get(`/apex/lesson/${lessonId}`),
          api.get('/apex/course'),
        ]);
        if (cancelled) return;
        setLesson(lRes.data.lesson);
        const done = new Set(cRes.data.progress?.completed_lesson_ids || []);
        setCompleted(done.has(lessonId));
        // Walk the course structure to find the next lesson id
        const flat = [];
        (cRes.data.course.modules || []).forEach(m => (m.lessons || []).forEach(l => flat.push(l.id)));
        const idx = flat.indexOf(lessonId);
        setNextLessonId(idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load lesson');
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId]);

  const markComplete = async () => {
    setSaving(true);
    try {
      await api.post('/apex/lesson/complete', { lesson_id: lessonId });
      setCompleted(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    if (nextLessonId) navigate(`/apex/course/lesson/${nextLessonId}`);
    else navigate('/apex/course');
  };

  if (err) return (
    <div className="min-h-screen bg-black text-white p-8">
      <p className="text-red-400" data-testid="apex-lesson-error">{err}</p>
      <Button className="mt-4" onClick={() => navigate('/apex/course')} data-testid="apex-lesson-back-btn">Back to Course</Button>
    </div>
  );
  if (!lesson) return <div className="min-h-screen bg-black text-white p-8" data-testid="apex-lesson-loading">Loading lesson…</div>;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-lesson-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button onClick={() => navigate('/apex/course')}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-2 mb-6"
                data-testid="apex-lesson-back-to-course">
          <ArrowLeft className="w-4 h-4" /> Back to Course
        </button>

        <div className="text-xs text-orange-500 uppercase tracking-widest mb-2">
          Module {lesson.module} · Lesson {lesson.order}
        </div>

        <article className="prose prose-invert prose-orange max-w-none prose-headings:text-white prose-p:text-gray-300 prose-strong:text-white prose-li:text-gray-300 prose-blockquote:border-l-orange-500 prose-blockquote:text-gray-200 prose-a:text-orange-400 prose-table:text-gray-300"
                 data-testid="apex-lesson-body">
          <ReactMarkdown>{lesson.body_md}</ReactMarkdown>
        </article>

        {lesson.key_takeaways?.length > 0 && (
          <Card className="mt-8 bg-[#111] border border-orange-500/30">
            <CardContent className="p-5">
              <div className="text-xs text-orange-400 uppercase tracking-widest mb-3 font-bold">Key Takeaways</div>
              <ul className="space-y-2">
                {lesson.key_takeaways.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          {!completed ? (
            <Button onClick={markComplete} disabled={saving}
                    className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                    data-testid="apex-lesson-complete-btn">
              {saving ? 'Saving…' : 'Mark Complete'}
            </Button>
          ) : (
            <div className="flex items-center gap-2 text-green-400 text-sm font-bold" data-testid="apex-lesson-completed-badge">
              <CheckCircle2 className="w-5 h-5" /> Completed
            </div>
          )}
          <Button onClick={goNext} variant="outline"
                  className="border-gray-700 hover:bg-gray-800 text-white"
                  data-testid="apex-lesson-next-btn">
            {nextLessonId ? 'Next Lesson' : 'Back to Course'} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
